<script setup lang="ts">
import { computed, ref } from "vue";
import { UiChip, UiText } from "@shopexperts/shared";
import { MAX_RULE_TERMS } from "@/app/composables/ban-list/banListForms";

const props = defineProps<{
    id?: string;
    disabled?: boolean;
    invalid?: boolean;
    error?: string;
}>();

const model = defineModel<string[]>({ default: () => [] });

const emit = defineEmits<{
    (e: "validate"): void;
}>();

const draft = ref("");

const isFull = computed(() => model.value.length >= MAX_RULE_TERMS);

const remainingLabel = computed(() => (
    isFull.value
        ? `Term limit reached (${MAX_RULE_TERMS})`
        : `${model.value.length} of ${MAX_RULE_TERMS} terms`
));

function addTerm() {
    const value = draft.value.trim();
    if (!value || props.disabled) return;

    if (isFull.value) return;

    // Screened text is lowercased before matching, so two terms that differ
    // only in case are the same term — drop the duplicate rather than send it.
    const alreadyPresent = model.value.some((term) => term.toLowerCase() === value.toLowerCase());
    if (alreadyPresent) {
        draft.value = "";
        return;
    }

    model.value = [...model.value, value];
    draft.value = "";
    emit("validate");
}

function removeTerm(index: number) {
    if (props.disabled) return;
    model.value = model.value.filter((_, i) => i !== index);
    emit("validate");
}

function onKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" || event.key === ",") {
        // Enter would submit the surrounding form before the term is committed.
        event.preventDefault();
        addTerm();
        return;
    }

    if (event.key === "Backspace" && !draft.value && model.value.length > 0) {
        removeTerm(model.value.length - 1);
    }
}
</script>

<template>
    <div class="flex flex-col gap-2">
        <input
            :id="props.id"
            v-model="draft"
            type="text"
            name="terms"
            :disabled="props.disabled || isFull"
            :placeholder="isFull ? 'Term limit reached' : 'Type a term and press Enter'"
            class="h-10 w-full rounded border px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-se-primary disabled:bg-se-gray-100"
            :class="props.invalid ? 'border-se-danger' : 'border-se-gray-300'"
            @keydown="onKeydown"
            @blur="addTerm"
        />

        <div v-if="model.length" class="flex flex-wrap items-center gap-2">
            <UiChip
                v-for="(term, index) in model"
                :key="`${term}-${index}`"
                :text="term"
                size="sm"
                rounded="full"
                variant="neutral"
                :icon="props.disabled ? undefined : 'close'"
                icon-after
                :class="props.disabled ? 'pointer-events-none opacity-70' : 'cursor-pointer'"
                @click="removeTerm(index)"
            />
        </div>

        <div class="flex flex-wrap items-center justify-between gap-2">
            <UiText
                as="p"
                variant="caption-normal"
                :class="props.error ? 'text-se-danger' : 'text-se-gray-600'"
                :text="props.error
                    || 'Every term must appear in the same field for the rule to fire. One term on its own only bans when it is the rule\'s only term.'"
            />
            <UiText as="p" variant="caption-normal" class="text-se-gray-500 shrink-0" :text="remainingLabel" />
        </div>
    </div>
</template>
